/**
 * Mutators for habit_nutrition_food table.
 *
 * All mutations require admin role - enforced on the server.
 * Client executes optimistically; server rejects unauthorized mutations.
 */

import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";

import { zql } from "../../schema";
import { requireAdmin } from "../../utils/auth";

/**
 * Update a single macro field on a nutrition food item.
 * Used for inline editing in the admin foods table.
 */
export const updateMacro = defineMutator(
  z.object({
    id: z.string(),
    field: z.enum([
      "calories",
      "protein",
      "fat",
      "carbs",
      "fiber",
      "saturatedFat",
    ]),
    value: z.number().nonnegative(),
  }),
  async ({ tx, ctx, args }) => {
    requireAdmin(ctx);

    const { id, field, value } = args;

    const existing = await tx.run(
      zql.habit_nutrition_food.where("id", id).one()
    );
    if (!existing) {
      return;
    }

    await tx.mutate.habit_nutrition_food.update({
      id,
      [field]: value,
    });
  }
);

/**
 * Insert a new nutrition food item.
 */
export const insert = defineMutator(
  z.object({
    id: z.string().nullable(),
    name: z.string(),
    normalizedName: z.string(),
    description: z.string().nullable().optional(),
    calories: z.number(),
    protein: z.number(),
    fat: z.number(),
    carbs: z.number(),
    fiber: z.number(),
    saturatedFat: z.number(),
    caloriesSource: z.string(),
    caloriesSourceId: z.string().nullable().optional(),
    caloriesConfidence: z.number(),
    proteinSource: z.string(),
    proteinSourceId: z.string().nullable().optional(),
    proteinConfidence: z.number(),
    fatSource: z.string(),
    fatSourceId: z.string().nullable().optional(),
    fatConfidence: z.number(),
    carbsSource: z.string(),
    carbsSourceId: z.string().nullable().optional(),
    carbsConfidence: z.number(),
    fiberSource: z.string(),
    fiberSourceId: z.string().nullable().optional(),
    fiberConfidence: z.number(),
    saturatedFatSource: z.string(),
    saturatedFatSourceId: z.string().nullable().optional(),
    saturatedFatConfidence: z.number(),
    foodGroup: z.string(),
    tags: z.array(z.string()),
    compatibleModifiers: z.array(z.string()),
    enrichmentStatus: z.string().nullable().optional(),
  }),
  async ({ tx, ctx, args }) => {
    requireAdmin(ctx);
    await tx.mutate.habit_nutrition_food.insert(args);
  }
);

/**
 * Delete a nutrition food item.
 */
export const remove = defineMutator(
  z.object({
    id: z.string(),
  }),
  async ({ tx, ctx, args }) => {
    requireAdmin(ctx);
    await tx.mutate.habit_nutrition_food.delete({ id: args.id });
  }
);
